import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';

type TimeRange = 'week' | 'month' | 'all';
type Vertical = 'all' | 'hvac' | 'plumbing' | 'electrical' | 'defi' | 'health' | 'creative';

interface LeaderEntry {
  rank: number;
  username: string;
  display_name: string;
  emoji: string;
  score: number;
  agents_count: number;
  badges_count: number;
  vertical: string;
  change: number; // rank change from previous period
  streak: number; // days on leaderboard
}

const FULL_LEADERBOARD: LeaderEntry[] = [
  { rank: 1, username: 'maria_health', display_name: 'Dr. Maria Chen', emoji: '👩🏻‍⚕️', score: 9210, agents_count: 4, badges_count: 5, vertical: 'health', change: 2, streak: 14 },
  { rank: 2, username: 'jordan_dev', display_name: 'Jordan', emoji: '🧔🏻', score: 8470, agents_count: 3, badges_count: 7, vertical: 'creative', change: 0, streak: 30 },
  { rank: 3, username: 'danny_hvac', display_name: "Danny's HVAC", emoji: '🔧', score: 5120, agents_count: 1, badges_count: 2, vertical: 'hvac', change: -1, streak: 7 },
  { rank: 4, username: 'sarah_plumbing', display_name: 'Sarah Pipes', emoji: '🔩', score: 4890, agents_count: 2, badges_count: 3, vertical: 'plumbing', change: 3, streak: 12 },
  { rank: 5, username: 'crypto_carl', display_name: 'DeFi Carl', emoji: '💹', score: 4650, agents_count: 5, badges_count: 4, vertical: 'defi', change: -2, streak: 21 },
  { rank: 6, username: 'sparky_electric', display_name: 'Sparky Co.', emoji: '⚡', score: 4200, agents_count: 2, badges_count: 2, vertical: 'electrical', change: 1, streak: 5 },
  { rank: 7, username: 'hvac_hero', display_name: 'HVAC Hero', emoji: '❄️', score: 3980, agents_count: 1, badges_count: 1, vertical: 'hvac', change: -1, streak: 9 },
  { rank: 8, username: 'dr_neuro', display_name: 'Dr. Neuro AI', emoji: '🧠', score: 3750, agents_count: 3, badges_count: 6, vertical: 'health', change: 4, streak: 3 },
  { rank: 9, username: 'pipe_master', display_name: 'Pipe Master', emoji: '🚿', score: 3500, agents_count: 1, badges_count: 1, vertical: 'plumbing', change: 0, streak: 15 },
  { rank: 10, username: 'nft_artist', display_name: 'NFT Artist Bot', emoji: '🎨', score: 3200, agents_count: 2, badges_count: 3, vertical: 'creative', change: -3, streak: 8 },
  { rank: 11, username: 'yield_farmer', display_name: 'Yield Farmer', emoji: '🌾', score: 2900, agents_count: 4, badges_count: 2, vertical: 'defi', change: 1, streak: 11 },
  { rank: 12, username: 'cool_breeze', display_name: 'Cool Breeze HVAC', emoji: '🌬️', score: 2750, agents_count: 1, badges_count: 1, vertical: 'hvac', change: 0, streak: 6 },
];

const VERTICALS: { label: string; value: Vertical; emoji: string }[] = [
  { label: 'All', value: 'all', emoji: '🌐' },
  { label: 'HVAC', value: 'hvac', emoji: '❄️' },
  { label: 'Plumbing', value: 'plumbing', emoji: '🔩' },
  { label: 'Electrical', value: 'electrical', emoji: '⚡' },
  { label: 'DeFi', value: 'defi', emoji: '💹' },
  { label: 'Health', value: 'health', emoji: '🏥' },
  { label: 'Creative', value: 'creative', emoji: '🎨' },
];

const TIME_RANGES: { label: string; value: TimeRange }[] = [
  { label: 'This Week', value: 'week' },
  { label: 'This Month', value: 'month' },
  { label: 'All Time', value: 'all' },
];

export function LeaderboardPage() {
  const [vertical, setVertical] = useState<Vertical>('all');
  const [timeRange, setTimeRange] = useState<TimeRange>('all');

  const filtered = useMemo(() => {
    let entries = FULL_LEADERBOARD;
    if (vertical !== 'all') {
      entries = entries.filter(e => e.vertical === vertical);
    }
    // Simulate time range by adjusting scores
    if (timeRange === 'week') {
      entries = entries.map(e => ({ ...e, score: Math.floor(e.score * 0.15) }));
    } else if (timeRange === 'month') {
      entries = entries.map(e => ({ ...e, score: Math.floor(e.score * 0.45) }));
    }
    // Re-rank after filtering
    return entries
      .sort((a, b) => b.score - a.score)
      .map((e, i) => ({ ...e, rank: i + 1 }));
  }, [vertical, timeRange]);

  const medalEmoji = (rank: number) => rank === 1 ? '🥇' : rank === 2 ? '🥈' : rank === 3 ? '🥉' : `#${rank}`;

  return (
    <div className="space-y-3">
      {/* Header */}
      <div className="social-box overflow-hidden">
        <div className="box-header flex items-center justify-between">
          <span>Leaderboard</span>
          <span className="text-2xs font-normal" style={{ color: 'var(--text-muted)' }}>
            {filtered.length} operator{filtered.length !== 1 ? 's' : ''}
          </span>
        </div>

        {/* Time Range Tabs */}
        <div className="flex gap-0" style={{ borderBottom: '1px solid var(--border-separator)' }}>
          {TIME_RANGES.map(t => (
            <button
              key={t.value}
              onClick={() => setTimeRange(t.value)}
              className="flex-1 py-1.5 text-2xs font-bold cursor-pointer text-center"
              style={{
                background: timeRange === t.value ? 'var(--accent)' : 'transparent',
                color: timeRange === t.value ? 'white' : 'var(--text-muted)',
                borderRight: '1px solid var(--border-separator)',
              }}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* Vertical Filter */}
        <div className="p-2 flex flex-wrap gap-1">
          {VERTICALS.map(v => (
            <button
              key={v.value}
              onClick={() => setVertical(v.value)}
              className="px-2 py-1 text-2xs font-bold cursor-pointer"
              style={{
                background: vertical === v.value ? 'var(--accent)' : 'var(--bg-filter)',
                color: vertical === v.value ? 'white' : 'var(--text-secondary)',
                border: `1px solid ${vertical === v.value ? 'var(--accent-hover)' : 'var(--border-separator)'}`,
              }}
            >
              {v.emoji} {v.label}
            </button>
          ))}
        </div>
      </div>

      {/* Top 3 Podium */}
      {filtered.length >= 3 && (
        <div className="social-box overflow-hidden">
          <div className="box-header">Top 3</div>
          <div className="flex items-end justify-center gap-3 p-4" style={{ background: 'linear-gradient(to bottom, #fefce8, var(--bg-card))' }}>
            {/* 2nd place */}
            <div className="text-center" style={{ width: '90px' }}>
              <div className="w-12 h-12 mx-auto flex items-center justify-center text-xl mb-1" style={{ background: '#e5e7eb', border: '2px solid #9ca3af' }}>
                {filtered[1].emoji}
              </div>
              <div className="text-xl">🥈</div>
              <Link to={`/@${filtered[1].username}`} className="font-bold text-2xs block truncate" style={{ color: 'var(--text-link)' }}>
                {filtered[1].display_name}
              </Link>
              <div className="text-2xs font-bold" style={{ color: '#16a34a' }}>{filtered[1].score.toLocaleString()}</div>
            </div>
            {/* 1st place */}
            <div className="text-center" style={{ width: '100px' }}>
              <div className="w-14 h-14 mx-auto flex items-center justify-center text-2xl mb-1" style={{ background: '#fef3c7', border: '2px solid #f59e0b' }}>
                {filtered[0].emoji}
              </div>
              <div className="text-2xl">🥇</div>
              <Link to={`/@${filtered[0].username}`} className="font-bold text-xs block truncate" style={{ color: 'var(--text-link)' }}>
                {filtered[0].display_name}
              </Link>
              <div className="text-xs font-bold" style={{ color: '#16a34a' }}>{filtered[0].score.toLocaleString()}</div>
            </div>
            {/* 3rd place */}
            <div className="text-center" style={{ width: '90px' }}>
              <div className="w-12 h-12 mx-auto flex items-center justify-center text-xl mb-1" style={{ background: '#fed7aa', border: '2px solid #f97316' }}>
                {filtered[2].emoji}
              </div>
              <div className="text-xl">🥉</div>
              <Link to={`/@${filtered[2].username}`} className="font-bold text-2xs block truncate" style={{ color: 'var(--text-link)' }}>
                {filtered[2].display_name}
              </Link>
              <div className="text-2xs font-bold" style={{ color: '#16a34a' }}>{filtered[2].score.toLocaleString()}</div>
            </div>
          </div>
        </div>
      )}

      {/* Full Table */}
      <div className="social-box overflow-hidden">
        <div className="box-header">Full Rankings</div>
        <table className="w-full text-xs text-left">
          <thead>
            <tr style={{ background: 'var(--bg-filter)', borderBottom: '1px solid var(--border-separator)' }}>
              <th className="p-2 font-bold" style={{ color: 'var(--text-muted)', width: '50px' }}>Rank</th>
              <th className="p-2 font-bold" style={{ color: 'var(--text-muted)' }}>Operator</th>
              <th className="p-2 font-bold text-center" style={{ color: 'var(--text-muted)' }}>Vertical</th>
              <th className="p-2 font-bold text-center" style={{ color: 'var(--text-muted)' }}>Agents</th>
              <th className="p-2 font-bold text-center" style={{ color: 'var(--text-muted)' }}>Badges</th>
              <th className="p-2 font-bold text-center" style={{ color: 'var(--text-muted)' }}>Streak</th>
              <th className="p-2 font-bold text-right" style={{ color: 'var(--text-muted)' }}>Score</th>
              <th className="p-2 font-bold text-right" style={{ color: 'var(--text-muted)', width: '50px' }}>+/-</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((entry, i) => (
              <tr
                key={entry.username}
                style={{
                  borderBottom: i < filtered.length - 1 ? '1px solid var(--border-separator)' : 'none',
                  background: entry.rank <= 3 ? 'rgba(253, 224, 71, 0.05)' : 'transparent',
                }}
                onMouseEnter={e => { e.currentTarget.style.background = 'var(--bg-card-hover)'; }}
                onMouseLeave={e => { e.currentTarget.style.background = entry.rank <= 3 ? 'rgba(253, 224, 71, 0.05)' : 'transparent'; }}
              >
                <td className="p-2 font-bold text-sm">{medalEmoji(entry.rank)}</td>
                <td className="p-2">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 flex items-center justify-center text-sm shrink-0" style={{ background: 'var(--bg-filter)', border: '1px solid var(--border-separator)' }}>
                      {entry.emoji}
                    </div>
                    <div>
                      <Link to={`/@${entry.username}`} className="font-bold text-xs no-underline" style={{ color: 'var(--text-link)' }}>
                        {entry.display_name}
                      </Link>
                      <div className="text-2xs" style={{ color: 'var(--text-muted)' }}>@{entry.username}</div>
                    </div>
                  </div>
                </td>
                <td className="p-2 text-center">
                  <span className="px-1.5 py-0.5 text-2xs font-bold" style={{ background: 'var(--bg-filter)', border: '1px solid var(--border-separator)', color: 'var(--text-secondary)', textTransform: 'uppercase' }}>
                    {entry.vertical}
                  </span>
                </td>
                <td className="p-2 text-center" style={{ color: 'var(--text-secondary)' }}>{entry.agents_count}</td>
                <td className="p-2 text-center" style={{ color: 'var(--text-secondary)' }}>{entry.badges_count}</td>
                <td className="p-2 text-center text-2xs" style={{ color: 'var(--text-muted)' }}>{entry.streak}d</td>
                <td className="p-2 text-right font-bold" style={{ color: '#16a34a' }}>{entry.score.toLocaleString()}</td>
                <td className="p-2 text-right text-2xs font-bold" style={{ color: entry.change > 0 ? '#16a34a' : entry.change < 0 ? '#dc2626' : 'var(--text-muted)' }}>
                  {entry.change > 0 ? `+${entry.change}` : entry.change < 0 ? `${entry.change}` : '-'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {filtered.length === 0 && (
          <div className="p-6 text-center">
            <div style={{ fontSize: '28px' }}>🏆</div>
            <div className="font-bold text-sm mt-1" style={{ color: 'var(--text-primary)' }}>No operators found</div>
            <div className="text-2xs mt-0.5" style={{ color: 'var(--text-muted)' }}>Try a different vertical or time range.</div>
          </div>
        )}
      </div>
    </div>
  );
}
